/**
 * @file The text of one stored report cell, by its column's registry format.
 * Consumers: `analyticsReportQueryView`, for the React run view and the PDF,
 * so both print the same text. Money is grouped whole dollars computed from
 * the raw decimal string, never through floating point, so no raw "0.0000"
 * reaches a reader. People and teams read the run's frozen `names` only and
 * fall back to the id; aging buckets read the live labels, then the retired
 * ones. A cell its format cannot read (or an unknown column) shows raw; null
 * gives null, and each renderer draws its own empty mark.
 */
import type { AnalyticsReportRunNames } from './Analytics.Report.types';
import type { NamedQueryCell, NamedQueryId } from './Analytics.NamedQuery.types';
import { ANALYTICS_AGING_BUCKET_LABELS } from './ANALYTICS_AGING_BUCKET_LABELS';
import { ANALYTICS_PROJECT_STAGE_LABELS } from './ANALYTICS_PROJECT_STAGE_LABELS';
import { ANALYTICS_RETIRED_AGING_BUCKET_LABELS } from './ANALYTICS_RETIRED_AGING_BUCKET_LABELS';
import { analyticsColumnPresentation } from './analyticsColumnPresentation';
import { analyticsCurrencyText } from './analyticsCurrencyText';
import { analyticsDecimalFraction } from './analyticsDecimalFraction';

const GROUPED = new Intl.NumberFormat('en-US');

const AGING_LABELS: Partial<Record<string, string>> = { ...ANALYTICS_RETIRED_AGING_BUCKET_LABELS, ...ANALYTICS_AGING_BUCKET_LABELS };

const STAGE_LABELS: Partial<Record<string, string>> = ANALYTICS_PROJECT_STAGE_LABELS;

export const analyticsColumnText = (
  query: NamedQueryId,
  column: string,
  cell: NamedQueryCell | undefined,
  names: AnalyticsReportRunNames | undefined,
): string | null => {
  if (cell === null || cell === undefined) return null;
  const raw = String(cell);
  switch (analyticsColumnPresentation(query, column).format) {
    case 'currency':
      return analyticsDecimalFraction(raw) === undefined ? raw : analyticsCurrencyText(raw);
    case 'count':
      return typeof cell === 'number' ? GROUPED.format(cell) : raw;
    case 'person':
      return names?.people[raw] ?? raw;
    case 'team':
      return names?.teams[raw] ?? raw;
    case 'agingBucket':
      return AGING_LABELS[raw] ?? raw;
    case 'stageList':
      return STAGE_LABELS[raw] ?? raw;
    case 'date':
    case 'text':
      return raw;
  }
};
